import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PlanDraftView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { planApi, planKeys } from '../api';
import { PlanPanel } from './plan-panel';
import { displayDateTime } from '../../../lib/date';

/**
 * Kế hoạch nháp — mở từ tab Điều phối, APM-ROLE-v.
 *
 * ## Vì sao lập kế hoạch nằm trong Điều phối, không phải một tab riêng
 *
 * Lên kế hoạch nháp là một **hành động**, không phải một nơi để ở. Người ta mở
 * nó khi đang nhìn ai gánh gì và thấy cần chia lại việc — tức đúng lúc đang ở
 * tab Điều phối. Một tab riêng bắt họ rời màn hình có câu trả lời để đi tới màn
 * hình đặt câu hỏi.
 *
 * ## Danh sách trước, chi tiết sau
 *
 * Bản trước chôn danh sách nháp trong một `<select>` cạnh tiêu đề, nên nháp cũ
 * gần như vô hình: người ta sinh nháp mới thay vì mở lại cái đã trả tiền. Ở đây
 * danh sách là thứ hiện ra đầu tiên.
 *
 * ## ⚠️ Không đổi mô hình dữ liệu
 *
 * Nháp vẫn là `ai_plan_draft` ở trạng thái chờ duyệt (luật #6), và
 * `PlanPanel` vẫn là màn hình chi tiết — chỉ đổi **đường vào**. Viết một bản
 * xem nháp thứ hai ở đây là tạo ra hai chỗ phải sửa mỗi lần đổi luồng duyệt.
 */
export function PlanDraftsDialog({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const drafts = useQuery({
    queryKey: planKeys.list(projectId),
    queryFn: () => planApi.list(projectId),
  });

  /**
   * Mở một nháp cụ thể, hoặc tạo nháp mới ⇒ giao cho `PlanPanel`.
   *
   * `key` theo `openId` để đổi nháp là **dựng lại** panel: nó giữ state nội bộ
   * (ô tinh chỉnh, ngày bắt đầu) theo từng nháp, và mang state của nháp này
   * sang nháp khác là cách âm thầm áp nhầm chỉ dẫn.
   */
  if (openId !== null || creating) {
    return (
      <Dialog label={t('plan.dialogTitle')} size="lg" onClose={onClose}>
        <Button
          size="sm"
          variant="ghost"
          className="self-start"
          onClick={() => {
            setOpenId(null);
            setCreating(false);
          }}
        >
          <ArrowLeft />
          {t('plan.backToList')}
        </Button>

        <PlanPanel
          key={openId ?? 'new'}
          projectId={projectId}
          {...(openId === null ? {} : { initialDraftId: openId })}
        />
      </Dialog>
    );
  }

  return (
    <Dialog label={t('plan.dialogTitle')} size="lg" onClose={onClose}>
      <header className="flex flex-wrap items-center gap-2">
        <Sparkles aria-hidden className="size-4 text-muted-foreground" />
        <h2 className="text-base font-semibold">{t('plan.dialogTitle')}</h2>
        <Button size="sm" className="ml-auto" onClick={() => setCreating(true)}>
          {t('plan.newDraft')}
        </Button>
      </header>
      <p className="text-xs text-muted-foreground">{t('plan.dialogHelp')}</p>

      {drafts.isPending ? (
        <Skeleton className="h-24" />
      ) : drafts.isError ? (
        <ErrorState message={errorMessage(drafts.error, t)} onRetry={() => void drafts.refetch()} />
      ) : drafts.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('plan.noDraft')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {drafts.data.map((draft) => (
            <li key={draft.id}>
              <DraftRow draft={draft} onOpen={() => setOpenId(draft.id)} />
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

export function DraftRow({
  draft,
  onOpen,
}: {
  draft: PlanDraftView;
  onOpen: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const kept = draft.items.filter((item) => !item.excluded).length;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full flex-col gap-1 rounded-DEFAULT border border-border px-3 py-2 text-left text-sm hover:bg-muted/60"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        {/*
          Trạng thái lên trước tiêu đề: "đang chờ bạn trả lời" là thứ quyết định
          có mở nháp này hay không, và nó phải đọc được mà không cần vào trong.
        */}
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
          {t(`plan.status.${draft.status}`)}
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {displayDateTime(draft.createdAt)}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {t('plan.draftSummary', { count: kept, hours: draft.totalEstimateHours })}
        </span>
      </div>

      {/* Yêu cầu gốc là thứ phân biệt hai nháp của cùng một dự án. Không có nó
          thì danh sách chỉ còn vài mốc thời gian giống hệt nhau. */}
      <span className="line-clamp-2 text-xs text-muted-foreground">{draft.prompt}</span>
    </button>
  );
}
